import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faTimes, faSyncAlt, faBook, faQuestionCircle,
  faFilter, faExclamationCircle, faSlidersH
} from '@fortawesome/free-solid-svg-icons';
import abacusService from '../../services/abacusService';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import '../../components/ui/HMSTable.css';

const HELP_CONTENT = {
  title: 'Chart of Accounts',
  body: 'This tab pulls the full chart of accounts from your connected Abacus ERP instance. You can optionally filter by account number range before loading. Once loaded, use the search bar to find accounts by ID, code, segment, category, or name. Account data is read-only and reflects the current state in Abacus.',
};

const HelpModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="md-modal-overlay" onClick={onClose}>
      <div className="md-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="md-modal-header" style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}>
          <div className="md-modal-header-text">
            <FontAwesomeIcon icon={faQuestionCircle} className="md-modal-header-icon" />
            <h2>{HELP_CONTENT.title}</h2>
          </div>
          <button className="md-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="md-modal-body">
          <p style={{ margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.7 }}>{HELP_CONTENT.body}</p>
        </div>
        <div className="md-modal-footer">
          <button className="md-btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const AbacusAccountsTab = ({ books }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rangeMin, setRangeMin] = useState('');
  const [rangeMax, setRangeMax] = useState('');
  const [error, setError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await abacusService.getAccounts(books);
      setAccounts(data.accounts || []);
      setLoaded(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch accounts from Abacus');
    } finally {
      setLoading(false);
    }
  }, [books]);

  const getDesignation = (account) => {
    if (!account.Designations) return '-';
    if (Array.isArray(account.Designations)) {
      const de = account.Designations.find(d => d.Language === 'de') || account.Designations[0];
      return de?.Text || de?.Name || '-';
    }
    if (typeof account.Designations === 'object') {
      return account.Designations.Text || account.Designations.Name || '-';
    }
    return String(account.Designations);
  };

  const filtered = accounts.filter(a => {
    const id = Number(a.Id);
    if (rangeMin && id < parseInt(rangeMin, 10)) return false;
    if (rangeMax && id > parseInt(rangeMax, 10)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      String(a.Id || '').includes(term) ||
      String(a.Code || '').toLowerCase().includes(term) ||
      String(a.Segment || '').toLowerCase().includes(term) ||
      String(a.Category || '').toLowerCase().includes(term) ||
      getDesignation(a).toLowerCase().includes(term)
    );
  });

  return (
    <>
      <div className="abacus-lookup-card">
        <div className="abacus-lookup-card__header">
          <FontAwesomeIcon icon={faSlidersH} style={{ color: '#0ea5e9', marginRight: 8 }} />
          <span>Account Range Filter</span>
        </div>
        <div className="abacus-lookup-card__body">
          <div className="abacus-filter-group">
            <label className="abacus-filter-label">Account Range</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                className="abacus-filter-input"
                placeholder="Min"
                value={rangeMin}
                onChange={(e) => setRangeMin(e.target.value)}
                style={{ width: 100 }}
              />
              <span style={{ color: '#9ca3af', fontSize: 12 }}>to</span>
              <input
                type="number"
                className="abacus-filter-input"
                placeholder="Max"
                value={rangeMax}
                onChange={(e) => setRangeMax(e.target.value)}
                style={{ width: 100 }}
              />
            </div>
          </div>
          <button className="abacus-action-btn abacus-action-btn--primary" onClick={fetchAccounts} disabled={loading}>
            <FontAwesomeIcon icon={loading ? faSyncAlt : faFilter} spin={loading} />
            {loaded ? 'Refresh' : 'Load Accounts'}
          </button>
        </div>
      </div>

      {loaded && (
        <div className="search-filter-controls">
          <div className="master-data-search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              className="master-data-search-input"
              placeholder="Search by ID, code, segment, category, or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <span className="abacus-result-count">{filtered.length} of {accounts.length} accounts</span>
        </div>
      )}

      {error && (
        <div className="abacus-error-banner">
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{error}</span>
        </div>
      )}

      <div className="invoice-list-container">
        <div className="abacus-card">
          <div className="abacus-section-header">
            <div className="abacus-section-header__left">
              <FontAwesomeIcon icon={faBook} style={{ color: '#0ea5e9' }} />
              <span className="abacus-section-header__title">Abacus Chart of Accounts</span>
              {loaded && <span className="abacus-section-header__count">{filtered.length}</span>}
            </div>
            <button className="abacus-section-header__help" title="About this tab" onClick={() => setHelpOpen(true)}>
              <FontAwesomeIcon icon={faQuestionCircle} />
            </button>
          </div>

          {!loaded && !loading ? (
            <div className="empty-state-container">
              <div className="empty-state-icon"><FontAwesomeIcon icon={faBook} /></div>
              <div className="empty-state-text">
                <h3>Load Accounts from Abacus</h3>
                <p>Set an optional account range filter and click "Load Accounts" to pull the chart of accounts from Abacus ERP.</p>
              </div>
            </div>
          ) : loading ? (
            <div className="abacus-card-loading">
              <HMSSkeleton lines={6} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state-container">
              <div className="empty-state-icon"><FontAwesomeIcon icon={faBook} /></div>
              <div className="empty-state-text">
                <h3>{searchTerm ? 'No Matching Accounts' : 'No Accounts Found'}</h3>
                <p>{searchTerm ? 'Try a different search term' : 'No accounts returned for the given range'}</p>
              </div>
            </div>
          ) : (
            <div className="hms-table-wrap">
              <table className="hms-table">
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Code</th>
                    <th scope="col">Name</th>
                    <th scope="col">Segment</th>
                    <th scope="col">Category</th>
                    <th scope="col">Currency</th>
                    <th scope="col" style={{ textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((acc) => (
                    <tr key={acc.Id}>
                      <td className="abacus-mono-cell">{acc.Id}</td>
                      <td className="invoice-number-cell">{acc.Code || '-'}</td>
                      <td>{getDesignation(acc)}</td>
                      <td>{acc.Segment || '-'}</td>
                      <td>{acc.Category || '-'}</td>
                      <td>{acc.CurrencyId || '-'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <HMSBadge status={acc.Status === 'Active' ? 'approved' : 'draft'}>
                          {acc.Status || 'N/A'}
                        </HMSBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default AbacusAccountsTab;
